import { ReadonlyVec3, ReadonlyVec4, vec2, vec3 } from 'gl-matrix';
import { ContourTexture } from '../../structure/ContourTexture';
import { CustomTargetObject } from './CustomTargetObject';
import { gl } from '../../../Engine';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { ImageObject } from '../../image/ImageObject';
import { Grid } from '../Grid';

/**
 * {@link TargetSphere} is a {@link CustomTargetObject} that represents a sphere in 3D space.\
 * It is defined by a center point and a diameter in millimeters.\
 * The mesh of the {@link TargetSphere} aligns with the {@link target} to simulate the actual result of a brush stroke.
 */
export class TargetSphere extends CustomTargetObject {
    private readonly margin = ContourTexture.DEFAULT_MARGIN;

    /**
     * Diameter of the sphere in millimeters.
     */
    protected diameter: number;

    /**
     * {@link FrameBufferObject} used to update the {@link ContourTexture}'s SDF.
     */
    protected fbo?: FrameBufferObject;

    /**
     * {@link SphereShader} used to render the sphere sdf data on the SDF texture.
     */
    protected readonly shader: SphereShader;

    /**
     * Constructor for the {@link TargetSphere} class.\
     * This constructor calls the {@link CustomTargetObject} constructor and initializes the {@link ContourTexture}.\
     * It also initializes the {@link shader} and generates the initial SDF.
     * @param target {@link SliceSource} defining the space that this {@link CustomTargetObject} might intersect with.
     * @param center {@link vec3 Vector} defining the center of the sphere in patient space.
     * @param diameter Diameter of the sphere in millimeters.
     * @param color Color of the sphere's outline.
     */
    public constructor(
        target: ImageObject,
        center: ReadonlyVec3,
        diameter: number,
        color: ReadonlyVec4
    ) {
        super(target);
        this.diameter = diameter;
        this._color = [color[0], color[1], color[2]];
        this._contourThickness = 0.5;
        this.renderOptions = { fillAlpha: 0.25 };
        this.shader = new SphereShader();

        const sizeInPixels = this.computeSizeInPixels();
        const corner = this.cornerCenteredOn(center, sizeInPixels);
        this._contourTexture = new ContourTexture(
            this._imageGrid,
            sizeInPixels,
            corner,
            this.margin
        );
        this.updateTexture();
    }

    public get grid(): Grid {
        return this._contourTexture!.textureGrid;
    }

    /**
     * Computes the grid size in pixels based on diameter, margin, and resolution.
     */
    private computeSizeInPixels(): vec3 {
        const { resX, resY } = this._imageGrid;
        const resZ = this._targetImage.parent.grid.resZ;
        const totalSize = this.diameter + 2 * this.margin;

        // Force an odd slice count so the grid has a well-defined
        // center voxel and the sphere SDF doesn't straddle two layers.
        let slices = Math.floor(totalSize / resZ) + 2;
        if (slices % 2 === 0) {
            slices += 1;
        }

        return vec3.fromValues(
            Math.ceil(totalSize / resX),
            Math.ceil(totalSize / resY),
            slices
        );
    }

    /**
     * PatientSpace corner of the sphere's grid of `sizeInPixels` voxels,
     * on the image's orientation and spacing, centered on `center`.
     */
    private cornerCenteredOn(
        center: ReadonlyVec3,
        sizeInPixels: ReadonlyVec3
    ): vec3 {
        const grid = this._imageGrid;
        return Grid.centeredOn(
            center,
            grid.getOrientation(),
            sizeInPixels,
            vec3.fromValues(grid.resX, grid.resY, grid.resZ)
        ).position;
    }

    /**
     * Updates the diameter of the sphere and rebuilds the grid.
     * @param size The new diameter in millimeters.
     */
    public resize(size: number): void {
        const currentCenter = vec3.clone(this.grid.center);
        this.diameter = size;
        const sizeInPixels = this.computeSizeInPixels();
        const corner = this.cornerCenteredOn(currentCenter, sizeInPixels);
        this._contourTexture!.updateGrid(sizeInPixels, corner);
        this.updateTexture();
    }

    /**
     * Updates the position of the sphere and rebuilds the grid.
     * @param position The new center position in patient space.
     */
    public setPosition(position: ReadonlyVec3): void {
        const sizeInPixels = this.grid.getSize();
        const corner = this.cornerCenteredOn(position, sizeInPixels);
        this._contourTexture!.updateGrid(sizeInPixels, corner);
        this.updateTexture();
    }

    /**
     * Method to update the SDF texture of this {@link TargetSphere}.
     */
    protected updateTexture() {
        const grid = this.grid;
        if (grid.isEmpty) {
            return;
        }

        const sdf = this._contourTexture!.signedDistanceField;
        const pixelSize = grid.getSize();
        const planarPixelSize = vec2.fromValues(pixelSize[0], pixelSize[1]);
        const orientationInverse = grid.getOrientationInverse();

        if (this.fbo) {
            if (!vec2.equals(this.fbo.size, planarPixelSize)) {
                this.fbo.resize(planarPixelSize);
            }
        } else {
            this.fbo = new FrameBufferObject(planarPixelSize);
        }
        this.fbo.bind();
        const vao = RenderStorage.getQuadVAO();
        vao.bind(0);
        gl.viewport(0, 0, sdf.size[0], sdf.size[1]);
        this.shader.start();
        const totalSize = 2 * this.margin + this.diameter;
        this.shader.margin.loadFloat((2 * this.margin) / totalSize);
        this.shader.radius.loadFloat(this.diameter / totalSize);

        // Sphere center's through-plane position, used as
        // reference for per-slice distance calculations.
        const centerThroughPlanePos = vec3.transformMat3(
            vec3.create(),
            grid.center,
            orientationInverse
        )[2];

        // Half-extent for normalizing through-plane distances
        // to [-1, 1], matching the in-plane texture coordinate
        // range so the shader produces a spherical SDF.
        const halfExtent = totalSize / 2;

        // Render the SDF for each texture layer. Use the texture
        // grid's own voxel positions (not image slice indices) to
        // avoid half-voxel misalignment when the grid corner falls
        // between image slices.
        for (let i = 0; i < sdf.size[2]; ++i) {
            this.fbo.createTextureLayer(sdf.texture, i);
            gl.clear(gl.COLOR_BUFFER_BIT);

            // Through-plane position of texture voxel i center,
            // queried directly from the texture grid.
            const voxelThroughPlanePos = grid.getSliceCenterNormalPosition(i);
            const throughPlaneDistance =
                voxelThroughPlanePos - centerThroughPlanePos;

            if (Math.abs(throughPlaneDistance) < halfExtent) {
                this.shader.normalDistance.loadFloat(
                    throughPlaneDistance / halfExtent
                );
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        }
        this.shader.stop();
        vao.unbind(0);
        this.fbo.unbind();
    }

    /**
     * @Override
     * Method to delete this {@link CustomTargetObject}.\
     * This calls the {@link CustomTargetObject.delete delete} method of the {@link CustomTargetObject} superclass, which deletes the ContourTexture.\
     * But it also {@link FrameBufferObject.delete deletes} the {@link fbo} and {@link shader}.\
     * It is important to call this method when the {@link TargetSphere} is no longer needed.
     * @returns True to indicate that the {@link CustomTargetObject} was successfully deleted.
     */
    public delete(): boolean {
        super.delete();
        this.shader.delete();
        if (this.fbo) {
            this.fbo.delete();
        }
        return true;
    }
}

/**
 * {@link SphereShader} is a {@link ShaderProgram} that is used to render the SDF texture of a {@link TargetSphere}.\
 * It renders the SDF texture as a sphere with a given {@link radius} and {@link margin}.
 */
class SphereShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;

    out vec2 textureCoords;

    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        textureCoords = position;
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es

    precision highp float;

    //Value in between -1 and 1
    in vec2 textureCoords;

    out float outColor;

    uniform float normalDistance;
    uniform float radius;
    uniform float margin;

    void main(){
        float distance = length(vec3(textureCoords, normalDistance));
        outColor = 0.5 - 0.5 * (distance - radius) / margin;
    }
    `;

    /**
     * Radius relative to texture size.\
     * radius is the distance at which values become 0.5.\
     * This should be half of the brush diameter.
     */
    public readonly radius: UniformFloat;

    /**
     * Margin relative to texture size.\
     * Margin is the distance from brush edge, at which the values turn to either 0 or 1.
     */
    public readonly margin: UniformFloat;

    /**
     * Distance of the current slice from the center of the sphere.\
     * This distance should be a value in between -1 and 1.
     */
    public readonly normalDistance: UniformFloat;

    public constructor() {
        super(
            SphereShader.VERTEX_CONTENT,
            SphereShader.FRAGMENT_CONTENT,
            'position'
        );
        this.radius = new UniformFloat('radius');
        this.margin = new UniformFloat('margin');
        this.normalDistance = new UniformFloat('normalDistance');
        this.storeUniformLocations(
            this.radius,
            this.margin,
            this.normalDistance
        );
    }
}
